import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Box,
  CreditCard,
  Eye,
  EyeOff,
  HelpCircle,
  LoaderCircle,
  Lock,
  MapPin,
  MessageSquare,
  Package,
  Phone,
  Receipt,
  Send,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { apiUrl } from "../config/Config";

const menuItems = [
  { key: "Profile", icon: UserRound },
  { key: "My Orders", icon: Package },
  { key: "Change Password", icon: Lock },
  // { key: "Downloads", icon: Box },
  { key: "My Questions", icon: HelpCircle },
  { key: "My Refunds", icon: Receipt },
  { key: "Need Help", icon: AlertCircle },
];

const emptyAddress = {
  name: "",
  country: "India",
  city: "",
  state: "",
  zip: "",
  street: "",
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const customerdata = useSelector((store) => store.userReducer.customerdata);
  const customerToken = customerdata?.token;

  const [activeTab, setActiveTab] = useState("Profile");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadingRefunds, setLoadingRefunds] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [savingRefund, setSavingRefund] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [addressType, setAddressType] = useState("Billing");
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [showPassword, setShowPassword] = useState({
    old: false,
    next: false,
    confirm: false,
  });
  const [profileForm, setProfileForm] = useState({
    fullname: "",
    email: "",
  });
  const [contactNumber, setContactNumber] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [questionForm, setQuestionForm] = useState({
    subject: "",
    message: "",
  });
  const [refundForm, setRefundForm] = useState({
    order_id: "",
    request_type: "Refund",
    reason: "",
    message: "",
  });

  const getAuthHeaders = useCallback(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + customerToken,
    },
  }), [customerToken]);

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const formatPaise = (amount) => formatCurrency(Number(amount || 0) / 100);

  const formatDate = (dateValue) => {
    if (!dateValue) return "NA";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatAddress = (address) => {
    if (!address) return "No address added yet";
    return [
      address?.name,
      address?.street,
      address?.city,
      address?.state,
      address?.zip,
      address?.country,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const profileCards = useMemo(() => {
    const totalOrders = orders.length;
    const paidOrders = orders.filter((item) => item?.payment_status === "paid").length;
    const totalSpent = orders.reduce(
      (sum, item) => sum + Number(item?.amount || item?.total || 0),
      0
    );

    return [
      { label: "Orders Placed", value: totalOrders, icon: Package },
      { label: "Successful Payments", value: paidOrders, icon: ShieldCheck },
      { label: "Saved Addresses", value: [profileData?.billingaddress, profileData?.shippingaddress].filter(Boolean).length, icon: MapPin },
      { label: "Total Spent", value: formatCurrency(totalSpent), icon: CreditCard },
    ];
  }, [orders, profileData]);

  const selectedOrder =
    orders.find((item) => String(item?.id) === String(selectedOrderId)) || orders[0] || null;

  const syncProfileState = (data) => {
    setProfileData(data);
    setProfileForm({
      fullname: data?.fullname || "",
      email: data?.email || "",
    });
    setContactNumber(data?.number || "");
  };

  const fetchProfileDetails = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const res = await axios.post(
        apiUrl + "getcustomerprofiledetails",
        {},
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        syncProfileState(res.data.data);
        dispatch({ type: "customerdata", payload: res.data.data });
      } else {
        throw new Error(res.data.message || "Unable to fetch profile");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to fetch profile details.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setLoadingProfile(false);
    }
  }, [dispatch, getAuthHeaders]);

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await axios.post(apiUrl + "getcustomerorders", {}, getAuthHeaders());
      if (res.data.status === "success") {
        const nextOrders = Array.isArray(res.data.data) ? res.data.data : [];
        setOrders(nextOrders);
        setSelectedOrderId(nextOrders[0]?.id || null);
      } else {
        throw new Error(res.data.message || "Unable to fetch orders");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to fetch orders.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setLoadingOrders(false);
    }
  }, [getAuthHeaders]);

  const fetchQuestions = useCallback(async () => {
    setLoadingQuestions(true);
    try {
      const res = await axios.post(apiUrl + "getcustomerquestions", {}, getAuthHeaders());
      if (res.data.status === "success") {
        setQuestions(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        throw new Error(res.data.message || "Unable to fetch questions");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to fetch questions.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setLoadingQuestions(false);
    }
  }, [getAuthHeaders]);

  const fetchRefunds = useCallback(async () => {
    setLoadingRefunds(true);
    try {
      const res = await axios.post(apiUrl + "getcustomerrefunds", {}, getAuthHeaders());
      if (res.data.status === "success") {
        setRefunds(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        throw new Error(res.data.message || "Unable to fetch refunds");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to fetch refunds.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setLoadingRefunds(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!customerToken) {
      navigate("/login", {
        replace: true,
        state: {
          from: location.pathname,
          redirectTo: "/profile",
        },
      });
      return;
    }

    fetchProfileDetails();
    fetchOrders();
    fetchQuestions();
    fetchRefunds();
  }, [customerToken, location.pathname, navigate, fetchProfileDetails, fetchOrders, fetchQuestions, fetchRefunds]);

  const handleMenuClick = (item) => {
    if (item === "Need Help") {
      setActiveTab(item);
      setShowMobileMenu(false);
      return;
    }
    setActiveTab(item);
    setShowMobileMenu(false);
  };

  const handleProfileSave = async () => {
    if (!profileForm.fullname || !profileForm.email) {
      Swal.fire({
        text: "Name and email are required",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingProfile(true);
    try {
      const res = await axios.post(
        apiUrl + "updatecustomerprofile",
        profileForm,
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        syncProfileState(res.data.data);
        dispatch({ type: "customerdata", payload: { ...customerdata, ...res.data.data } });
        Swal.fire({
          text: "Profile updated successfully",
          icon: "success",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      } else {
        throw new Error(res.data.message || "Unable to update profile");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to update profile.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleContactSave = async () => {
    if (!contactNumber) {
      Swal.fire({
        text: "Please enter a contact number",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingContact(true);
    try {
      const res = await axios.post(
        apiUrl + "updatecustomercontact",
        { number: contactNumber },
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        const nextProfile = {
          ...(profileData || {}),
          number: res.data.data?.number || contactNumber,
        };
        syncProfileState(nextProfile);
        dispatch({ type: "customerdata", payload: { ...customerdata, ...nextProfile } });
        Swal.fire({
          text: "Contact updated successfully",
          icon: "success",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      } else {
        throw new Error(res.data.message || "Unable to update contact");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to update contact.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingContact(false);
    }
  };

  const openAddressModal = (type) => {
    const currentAddress =
      type === "Shipping" ? profileData?.shippingaddress : profileData?.billingaddress;
    setAddressType(type);
    setAddressForm({
      name: currentAddress?.name || profileData?.fullname || "",
      country: currentAddress?.country || "India",
      city: currentAddress?.city || profileData?.city || "",
      state: currentAddress?.state || profileData?.state || "",
      zip: currentAddress?.zip || profileData?.pincode || "",
      street: currentAddress?.street || profileData?.address || "",
    });
    setShowAddressModal(true);
  };

  const handleSaveAddress = async () => {
    if (
      !addressForm.name ||
      !addressForm.street ||
      !addressForm.city ||
      !addressForm.state ||
      !addressForm.zip
    ) {
      Swal.fire({
        text: "Please complete all address fields",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingAddress(true);
    try {
      const res = await axios.post(
        apiUrl + "updatecustomeraddress",
        { type: addressType, ...addressForm },
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        const nextProfile = {
          ...(profileData || {}),
          billingaddress: res.data.data?.billingaddress,
          shippingaddress: res.data.data?.shippingaddress,
        };
        syncProfileState(nextProfile);
        dispatch({ type: "customerdata", payload: { ...customerdata, ...nextProfile } });
        setShowAddressModal(false);
      } else {
        throw new Error(res.data.message || "Unable to update address");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to update address.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingAddress(false);
    }
  };

  const handlePasswordSave = async () => {
    if (
      !passwordForm.oldPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      Swal.fire({
        text: "Please fill all password fields",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      Swal.fire({
        text: "New password and confirm password do not match",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingPassword(true);
    try {
      const res = await axios.post(
        apiUrl + "changecustomerpassword",
        {
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword,
          confirm_password: passwordForm.confirmPassword,
        },
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        setPasswordForm({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        Swal.fire({
          text: "Password updated successfully",
          icon: "success",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      } else {
        throw new Error(res.data.message || "Unable to update password");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to update password.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!questionForm.subject || !questionForm.message) {
      Swal.fire({
        text: "Please enter subject and message",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingQuestion(true);
    try {
      const res = await axios.post(
        apiUrl + "submitcustomerquestion",
        questionForm,
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        setQuestionForm({ subject: "", message: "" });
        await fetchQuestions();
        setActiveTab("My Questions");
        Swal.fire({
          text: "Your question has been submitted",
          icon: "success",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      } else {
        throw new Error(res.data.message || "Unable to submit question");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to submit question.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleSubmitRefund = async () => {
    if (!refundForm.order_id || !refundForm.reason || !refundForm.message) {
      Swal.fire({
        text: "Please select order, reason and message",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSavingRefund(true);
    try {
      const res = await axios.post(
        apiUrl + "submitcustomerrefund",
        refundForm,
        getAuthHeaders()
      );
      if (res.data.status === "success") {
        setRefundForm({
          order_id: "",
          request_type: "Refund",
          reason: "",
          message: "",
        });
        await fetchRefunds();
        Swal.fire({
          text: "Your refund request has been submitted",
          icon: "success",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      } else {
        throw new Error(res.data.message || "Unable to submit refund request");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to submit refund request.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setSavingRefund(false);
    }
  };

  const renderProfileTab = () => {
    const initials = (profileData?.fullname || "T")
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    return (
      <div className="profile-shell">
        <section className="profile-hero-card">
          <div className="profile-hero-copy">
            <span className="profile-hero-kicker">Customer Dashboard</span>
            <h1>{profileData?.fullname || "Your profile"}</h1>
            <p>
              Keep your account details updated, review recent orders, and manage
              your checkout information from one place.
            </p>
            <div className="profile-hero-tags">
              <span>
                <ShieldCheck size={16} />
                Verified account
              </span>
              <span>
                <Phone size={16} />
                {profileData?.number || "No contact number"}
              </span>
            </div>
          </div>
          <div className="profile-hero-avatar">
            <div className="profile-hero-avatar-badge">{initials}</div>
            <p>{profileData?.email || "No email address"}</p>
          </div>
        </section>

        <section className="profile-stat-grid">
          {profileCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="profile-stat-card">
                <div className="profile-stat-icon">
                  <Icon size={18} />
                </div>
                <div>
                  <p>{card.label}</p>
                  <strong>{card.value}</strong>
                </div>
              </div>
            );
          })}
        </section>

        <section className="profile-grid">
          <div className="profile-panel">
            <div className="profile-panel-head">
              <div>
                <span className="profile-panel-kicker">Account</span>
                <h2>Personal details</h2>
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="profile-page-form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profileForm.fullname}
                  onChange={(event) =>
                    setProfileForm((prev) => ({
                      ...prev,
                      fullname: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="profile-page-form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(event) =>
                    setProfileForm((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="profile-panel-footer">
              <button
                className="profile-page-save-btn"
                onClick={handleProfileSave}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving..." : "Save Details"}
              </button>
            </div>
          </div>

          <div className="profile-panel">
            <div className="profile-panel-head">
              <div>
                <span className="profile-panel-kicker">Contact</span>
                <h2>Mobile number</h2>
              </div>
            </div>

            <div className="profile-page-form-group">
              <label>Contact Number</label>
              <input
                type="text"
                value={contactNumber}
                onChange={(event) => setContactNumber(event.target.value)}
                placeholder="Enter mobile number"
              />
            </div>

            <div className="profile-panel-inline-note">
              This number is used for checkout updates and order communication.
            </div>

            <div className="profile-panel-footer">
              <button
                className="profile-page-save-btn"
                onClick={handleContactSave}
                disabled={savingContact}
              >
                {savingContact ? "Updating..." : "Update Contact"}
              </button>
            </div>
          </div>
        </section>

        <section className="profile-panel">
          <div className="profile-panel-head">
            <div>
              <span className="profile-panel-kicker">Saved Addresses</span>
              <h2>Billing and shipping</h2>
            </div>
          </div>

          <div className="profile-address-grid">
            <div className="profile-address-card">
              <div className="profile-address-card-head">
                <div>
                  <span>Billing Address</span>
                  <strong>{profileData?.billingaddress?.name || "Billing"}</strong>
                </div>
                <button
                  className="profile-page-contact-update-btn"
                  onClick={() => openAddressModal("Billing")}
                >
                  Edit
                </button>
              </div>
              <p>{formatAddress(profileData?.billingaddress)}</p>
            </div>

            <div className="profile-address-card">
              <div className="profile-address-card-head">
                <div>
                  <span>Shipping Address</span>
                  <strong>{profileData?.shippingaddress?.name || "Shipping"}</strong>
                </div>
                <button
                  className="profile-page-contact-update-btn"
                  onClick={() => openAddressModal("Shipping")}
                >
                  Edit
                </button>
              </div>
              <p>{formatAddress(profileData?.shippingaddress)}</p>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderOrdersTab = () => {
    if (loadingOrders) {
      return (
        <div className="profile-loading-state">
          <LoaderCircle className="spin" size={28} />
          <p>Loading your orders...</p>
        </div>
      );
    }

    if (!orders.length) {
      return (
        <div className="profile-empty-state">
          <Package size={32} />
          <h3>No orders yet</h3>
          <p>Your successful Razorpay orders will appear here.</p>
        </div>
      );
    }

    return (
      <div className="profile-orders-shell">
        <div className="profile-orders-list">
          {orders.map((order) => (
            <button
              key={order.id}
              className={`profile-order-card ${
                String(selectedOrder?.id) === String(order.id) ? "active" : ""
              }`}
              onClick={() => setSelectedOrderId(order.id)}
            >
              <div className="profile-order-card-top">
                <strong>{order.order_number || `Order #${order.id}`}</strong>
                <span>{order.status || "created"}</span>
              </div>
              <div className="profile-order-card-row">
                <span>{formatDate(order.created_at)}</span>
                <span>{formatPaise(order.amount_paise)}</span>
              </div>
              <p>{order.delivery_schedule || "Delivery schedule not selected"}</p>
            </button>
          ))}
        </div>

        <div className="profile-order-details">
          <div className="profile-order-details-head">
            <div>
              <span className="profile-panel-kicker">Order Details</span>
              <h2>{selectedOrder?.order_number || `Order #${selectedOrder?.id}`}</h2>
            </div>
            <div className="profile-order-status-pills">
              <span>{selectedOrder?.status || "created"}</span>
              <span>{selectedOrder?.payment_status || "pending"}</span>
            </div>
          </div>

          {selectedOrder?.invoice_url && (
            <div className="profile-order-actions">
              <a
                href={selectedOrder.invoice_url}
                target="_blank"
                rel="noreferrer"
                className="profile-order-invoice-link"
              >
                <Receipt size={16} />
                View Invoice - {formatDate(selectedOrder?.verified_at || selectedOrder?.created_at)}
              </a>
            </div>
          )}

          <div className="profile-order-metrics">
            <div>
              <label>Order Date</label><br />
              <strong>{formatDate(selectedOrder?.created_at)}</strong>
            </div>
            <div>
              <label>Payment Method</label>
              <strong>{selectedOrder?.payment_method || "Razorpay"}</strong>
            </div>
            {/* <div>
              <label>Delivery Slot</label>
              <strong>{selectedOrder?.delivery_schedule || "NA"}</strong>
            </div> */}
            <div>
              <label>Order Total</label><br />
              <strong>{formatCurrency(selectedOrder?.amount)}</strong>
            </div>
            <div>
              <label>Paid Amount</label><br />
              <strong>{formatPaise(selectedOrder?.amount_paise)}</strong>
            </div>
          </div>

          <div className="profile-order-addresses">
            <div className="profile-order-address-card">
              <h3>Shipping Address</h3>
              <p>{formatAddress(selectedOrder?.shipping_address)}</p>
            </div>
            <div className="profile-order-address-card">
              <h3>Billing Address</h3>
              <p>{formatAddress(selectedOrder?.billing_address)}</p>
            </div>
          </div>

          <div className="profile-order-summary-grid">
            <div>
              <label>Subtotal</label><br />
              <strong>{formatCurrency(selectedOrder?.subtotal)}</strong>
            </div>
            <div>
              <label>Tax</label><br />
              <strong>{formatCurrency(selectedOrder?.tax)}</strong>
            </div>
            {/* <div>
              <label>Shipping</label>
              <strong>{formatCurrency(selectedOrder?.shipping)}</strong>
            </div> */}
            <div>
              <label>Razorpay Payment</label><br />
              <strong>{selectedOrder?.razorpay_payment_id || "Pending"}</strong>
            </div>
          </div>

          <div className="profile-order-items-panel">
            <div className="profile-order-items-head">
              <h3>Items</h3>
              <span>{selectedOrder?.items?.length || 0} item(s)</span>
            </div>
            <div className="profile-order-item-table">
              {(selectedOrder?.items || []).map((item, index) => (
                <div key={item?.product_id || item?.id || index} className="profile-order-item-row">
                  <div>
                    <strong>{item?.name || "Product"}</strong>
                    <p>Qty: {item?.quantity || 0}</p>
                  </div>
                  <span>{formatCurrency(item?.price)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChangePasswordTab = () => {
    return (
      <div className="profile-panel profile-password-panel">
        <div className="profile-panel-head">
          <div>
            <span className="profile-panel-kicker">Security</span>
            <h2>Change Password</h2>
          </div>
        </div>

        {[
          ["oldPassword", "Old Password", "old"],
          ["newPassword", "New Password", "next"],
          ["confirmPassword", "Confirm Password", "confirm"],
        ].map(([key, label, visibilityKey]) => (
          <div className="profile-page-form-group" key={key}>
            <label className="profile-page-input-label">{label}</label>
            <div className="profile-page-password-input-wrapper">
              <input
                type={showPassword[visibilityKey] ? "text" : "password"}
                value={passwordForm[key]}
                onChange={(event) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    [key]: event.target.value,
                  }))
                }
              />
              <button
                type="button"
                className="profile-page-password-toggle"
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    [visibilityKey]: !prev[visibilityKey],
                  }))
                }
                aria-label={`Toggle ${label}`}
              >
                {showPassword[visibilityKey] ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>
        ))}

        <div className="profile-panel-footer">
          <button
            className="profile-page-save-btn"
            onClick={handlePasswordSave}
            disabled={savingPassword}
          >
            {savingPassword ? "Updating..." : "Update Password"}
          </button>
        </div>
      </div>
    );
  };

  const renderPlaceholderTab = (title, description) => {
    return (
      <div className="profile-empty-state">
        <Box size={30} />
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    );
  };

  const renderQuestionsTab = () => {
    if (loadingQuestions) {
      return (
        <div className="profile-loading-state">
          <LoaderCircle className="spin" size={28} />
          <p>Loading your questions...</p>
        </div>
      );
    }

    if (!questions.length) {
      return (
        <div className="profile-empty-state">
          <MessageSquare size={30} />
          <h3>No questions yet</h3>
          <p>Questions you submit from Need Help will appear here.</p>
        </div>
      );
    }

    return (
      <div className="profile-question-list">
        {questions.map((question) => (
          <div className="profile-question-card" key={question.id}>
            <div className="profile-question-card-head">
              <div>
                <span>{formatDate(question.created_at)}</span>
                <h3>{question.subject}</h3>
              </div>
              <strong className={`profile-question-status ${question.status}`}>
                {question.status || "open"}
              </strong>
            </div>
            <p className="profile-question-message">{question.message}</p>
            <div className="profile-question-reply">
              <span>Admin Reply</span>
              <p>{question.admin_reply || "Waiting for admin reply."}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRefundsTab = () => {
    const paidOrders = orders.filter((order) => order?.payment_status === "paid");

    return (
      <div className="profile-refund-shell">
        <div className="profile-panel profile-refund-panel">
          <div className="profile-panel-head">
            <div>
              <span className="profile-panel-kicker">Refund Request</span>
              <h2>Request Refund / Return</h2>
            </div>
          </div>
          <br />
          <div className="profile-help-form">
            <div className="profile-page-form-group">
              <label>Order</label>
              <select
                value={refundForm.order_id}
                onChange={(event) =>
                  setRefundForm((prev) => ({
                    ...prev,
                    order_id: event.target.value,
                  }))
                }
                className="form-control"
              >
                <option value="">Select order</option>
                {paidOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.order_number || `Order #${order.id}`} - {formatCurrency(order.amount)}
                  </option>
                ))}
              </select>
            </div>

            <div className="profile-form-grid">
              <div className="profile-page-form-group">
                <label>Request Type</label>
                <select
                  value={refundForm.request_type}
                  onChange={(event) =>
                    setRefundForm((prev) => ({
                      ...prev,
                      request_type: event.target.value,
                    }))
                  }
                  className="form-control"
                >
                  <option value="Refund">Refund</option>
                  <option value="Return">Return</option>
                  <option value="Exchange">Exchange</option>
                </select>
              </div>
              <div className="profile-page-form-group">
                <label>Reason</label>
                <input
                  type="text"
                  value={refundForm.reason}
                  onChange={(event) =>
                    setRefundForm((prev) => ({
                      ...prev,
                      reason: event.target.value,
                    }))
                  }
                  placeholder="Example: Product damaged"
                  className="form-control"
                />
              </div>
            </div>

            <div className="profile-page-form-group">
              <label>Message</label>
              <textarea
                value={refundForm.message}
                onChange={(event) =>
                  setRefundForm((prev) => ({
                    ...prev,
                    message: event.target.value,
                  }))
                }
                placeholder="Share details about your refund request"
              />
            </div>
          </div>

          <div className="profile-panel-footer">
            <button
              className="profile-page-save-btn"
              onClick={handleSubmitRefund}
              disabled={savingRefund || !paidOrders.length}
            >
              <Send size={16} />
              {savingRefund ? "Submitting..." : "Submit Refund Request"}
            </button>
          </div>
        </div>

        <div className="profile-refund-list-wrap">
          <div className="profile-panel-head">
            <div>
              <span className="profile-panel-kicker">My Refunds</span>
              <h2>Request Updates</h2>
            </div>
          </div>

          {loadingRefunds ? (
            <div className="profile-loading-state">
              <LoaderCircle className="spin" size={28} />
              <p>Loading your refund requests...</p>
            </div>
          ) : refunds.length ? (
            <div className="profile-question-list">
              {refunds.map((refund) => (
                <div className="profile-question-card profile-refund-card" key={refund.id}>
                  <div className="profile-question-card-head">
                    <div>
                      <span>{formatDate(refund.created_at)}</span>
                      <h3>{refund.request_type} - {refund.order_number || `Order #${refund.order_id}`}</h3>
                    </div>
                    <strong className={`profile-question-status ${refund.status}`}>
                      {refund.status || "requested"}
                    </strong>
                  </div>
                  <p className="profile-question-message">
                    <strong>{refund.reason}</strong><br />
                    {refund.message}
                  </p>
                  <div className="profile-question-reply">
                    <span>Admin Response</span>
                    <p>{refund.admin_response || "Waiting for admin response."}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="profile-empty-state">
              <Receipt size={30} />
              <h3>No refund requests yet</h3>
              <p>Submit your first request from the form above.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNeedHelpTab = () => {
    return (
      <div className="profile-panel profile-help-panel">
        <div className="profile-panel-head">
          <div>
            <span className="profile-panel-kicker">Support</span>
            <h2>Need Help?</h2>
          </div>
        </div>

        <div className="profile-help-form">
          <div className="profile-page-form-group">
            <label>Subject</label>
            <input
              type="text"
              value={questionForm.subject}
              onChange={(event) =>
                setQuestionForm((prev) => ({
                  ...prev,
                  subject: event.target.value,
                }))
              }
              placeholder="Example: Need help with my order"
            />
          </div>
          <div className="profile-page-form-group">
            <label>Message</label>
            <textarea
              value={questionForm.message}
              onChange={(event) =>
                setQuestionForm((prev) => ({
                  ...prev,
                  message: event.target.value,
                }))
              }
              placeholder="Write your question here"
            />
          </div>
        </div>

        <div className="profile-panel-footer">
          <button
            className="profile-page-save-btn"
            onClick={handleSubmitQuestion}
            disabled={savingQuestion}
          >
            <Send size={16} />
            {savingQuestion ? "Submitting..." : "Submit Query"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="profile-page profile-page-v2">
      <div className="profile-page-container">
        <aside className="profile-page-sidebar">
          <div
            className="profile-mobile-filter-btn"
            onClick={() => setShowMobileMenu((prev) => !prev)}
          >
            <span>Menu</span>
          </div>

          <div className="profile-side-summary">
            <p className="profile-side-summary-label">Welcome back</p>
            <h2>{profileData?.fullname || customerdata?.fullname || "Customer"}</h2>
            <span>{profileData?.email || customerdata?.email || "No email added"}</span>
          </div>

          <ul className={`profile-page-sidebar-menu ${showMobileMenu ? "show" : ""}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.key}
                  className={activeTab === item.key ? "active" : ""}
                  onClick={() => handleMenuClick(item.key)}
                >
                  <span className="profile-menu-item">
                    <Icon size={16} />
                    {item.key}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        <main className="profile-page-content">
          {loadingProfile ? (
            <div className="profile-loading-state">
              <LoaderCircle className="spin" size={28} />
              <p>Loading your profile...</p>
            </div>
          ) : (
            <>
              {activeTab === "Profile" && renderProfileTab()}
              {activeTab === "My Orders" && renderOrdersTab()}
              {activeTab === "Change Password" && renderChangePasswordTab()}
              {activeTab === "Downloads" &&
                renderPlaceholderTab(
                  "Downloads",
                  "Digital downloads are not enabled for this account yet."
                )}
              {activeTab === "My Questions" && renderQuestionsTab()}
              {activeTab === "My Refunds" && renderRefundsTab()}
              {activeTab === "Need Help" && renderNeedHelpTab()}
            </>
          )}
        </main>
      </div>

      {showAddressModal && (
        <div
          className="profile-page-modal-backdrop-address"
          onClick={() => setShowAddressModal(false)}
        >
          <div
            className="profile-page-modal-content-address profile-page-address-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="profile-page-modal-close"
              onClick={() => setShowAddressModal(false)}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            <h3 className="profile-page-modal-title">
              {addressType} Address
            </h3>
            <div className="profile-page-address-form">
              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Type</label>
                <div className="profile-page-address-radio-group">
                  {["Billing", "Shipping"].map((type) => (
                    <label className="profile-page-address-radio-label" key={type}>
                      <input
                        type="radio"
                        name="addressType"
                        value={type}
                        checked={addressType === type}
                        onChange={() => setAddressType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>

              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Receiver Name</label>
                <input
                  type="text"
                  value={addressForm.name}
                  onChange={(event) =>
                    setAddressForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </div>

              <div className="profile-page-form-group-row">
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">Country</label>
                  <input
                    type="text"
                    value={addressForm.country}
                    onChange={(event) =>
                      setAddressForm((prev) => ({ ...prev, country: event.target.value }))
                    }
                  />
                </div>
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">City</label>
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={(event) =>
                      setAddressForm((prev) => ({ ...prev, city: event.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="profile-page-form-group-row">
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">State</label>
                  <input
                    type="text"
                    value={addressForm.state}
                    onChange={(event) =>
                      setAddressForm((prev) => ({ ...prev, state: event.target.value }))
                    }
                  />
                </div>
                <div className="profile-page-form-group-half">
                  <label className="profile-page-input-label">ZIP</label>
                  <input
                    type="text"
                    value={addressForm.zip}
                    onChange={(event) =>
                      setAddressForm((prev) => ({ ...prev, zip: event.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="profile-page-form-group">
                <label className="profile-page-input-label">Street Address</label>
                <textarea
                  rows="4"
                  value={addressForm.street}
                  onChange={(event) =>
                    setAddressForm((prev) => ({ ...prev, street: event.target.value }))
                  }
                />
              </div>

              <button
                className="profile-page-save-btn profile-page-address-update-btn"
                onClick={handleSaveAddress}
                disabled={savingAddress}
              >
                {savingAddress ? "Saving..." : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
